import * as React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, type SubmitHandler, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { removeEmptyFields } from '@/lib/utils';
import { readFormDraft, useFormDraft } from '@/lib/hooks';
import { matterSchema, uzbekPhoneRegex, type MatterFormValues } from '../schemas';
import type { CreateMatterDto } from '../types';
import { useMatter, useMatterMutations } from '../hooks';

// Legacy matters may store the court phone unformatted (e.g. "+998901234567").
// Re-shape it to the form's "+998 (xx) xxx xx xx" pattern when possible so the
// loaded value passes validation; unrecognizable values are kept as-is (the
// court-case form then shows the inline error on the field).
function normalizeUzbekPhone(raw?: string | null): string {
  if (!raw) return '';
  if (uzbekPhoneRegex.test(raw)) return raw;
  const digits = raw.replace(/\D/g, '');
  const local =
    digits.length === 12 && digits.startsWith('998')
      ? digits.slice(3)
      : digits.length === 9
        ? digits
        : null;
  if (!local) return raw;
  return `+998 (${local.slice(0, 2)}) ${local.slice(2, 5)} ${local.slice(5, 7)} ${local.slice(7, 9)}`;
}

// Create-form draft persisted to localStorage so a refresh / accidental exit
// doesn't lose the user's input. Create mode only — never applied when editing.
const MATTER_DRAFT_KEY = 'matter-create-draft';

interface MatterDraftExtra {
  selectedClientId?: number;
  selectedAttorney?: number;
  selectedStaffs?: number[];
  selectedPartner?: number;
  isMdManaged?: boolean;
}

export function useMatterForm() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEditMode = !!id;

  const contractIdParam = searchParams.get('contractId');
  const contractIdFromUrl = contractIdParam ? Number(contractIdParam) : undefined;

  const { data: existingMatter, isLoading: isLoadingMatter } = useMatter(id);
  const { createMutation, updateMutation } = useMatterMutations();

  // Restore an unfinished create-form draft (read once, before the first
  // render, so it lands as defaultValues — no reset needed).
  const [draft] = React.useState(() =>
    isEditMode
      ? null
      : readFormDraft<Partial<MatterFormValues>, MatterDraftExtra>(MATTER_DRAFT_KEY),
  );

  const [selectedClientId, setSelectedClientId] = React.useState<number | undefined>(
    draft?.extra?.selectedClientId,
  );
  const [selectedAttorney, setSelectedAttorney] = React.useState<number | undefined>(
    draft?.extra?.selectedAttorney,
  );
  const [selectedStaffs, setSelectedStaffs] = React.useState<number[]>(
    draft?.extra?.selectedStaffs ?? [],
  );
  const [selectedPartner, setSelectedPartner] = React.useState<number | undefined>(
    draft?.extra?.selectedPartner,
  );
  // MD-managed case — when checked, partner/lead/staff are all optional.
  const [isMdManaged, setIsMdManaged] = React.useState(draft?.extra?.isMdManaged ?? false);

  const form = useForm<MatterFormValues>({
    resolver: zodResolver(matterSchema),
    defaultValues: {
      isCourtCase: false,
      category: undefined as unknown as MatterFormValues['category'],
      caseNumber: '',
      plainTiff: '',
      defendant: '',
      description: '',
      judgeName: '',
      courtName: '',
      courtEmail: '',
      courtPhone: '',
      clientStatus: 'ACCUSED',
      clientId: undefined,
      practiceAreaId: undefined as unknown as number,
      scopeIn: '',
      scopeOut: '',
      estimate: '',
      budget: '',
      targetEndDate: '',
      riskLevel: undefined,
      ...(draft?.values ?? {}),
      // An explicit ?contractId= link always wins over a stale draft.
      contractId: (contractIdFromUrl ?? draft?.values?.contractId) as number,
    },
  });

  const { clearDraft } = useFormDraft({
    key: MATTER_DRAFT_KEY,
    form,
    enabled: !isEditMode,
    extra: React.useMemo(
      () => ({ selectedClientId, selectedAttorney, selectedStaffs, selectedPartner, isMdManaged }),
      [selectedClientId, selectedAttorney, selectedStaffs, selectedPartner, isMdManaged],
    ),
  });

  const isCourtCase = form.watch('isCourtCase');

  // The court-case flag drives Gate 3 / court instances — it may only change
  // while the matter is still in INTAKE (server-enforced on update too).
  const lockCourtCase =
    isEditMode && !!existingMatter && existingMatter?.phase !== 'INTAKE';

  // From PLANNING the edit form locks contract, client, the plan fields and
  // risk (server silently drops them too) — the plan then changes via the Plan
  // tab (until Gate 2) or a Change Request, risk via the Compliance dialog.
  const lockPostIntake = lockCourtCase;

  // Load existing matter data in edit mode
  React.useEffect(() => {
    if (existingMatter) {
      setSelectedClientId(existingMatter.clientId);
      setSelectedAttorney(existingMatter.responsibleAttorneys?.[0]?.id);
      setSelectedStaffs(existingMatter.staffs?.map((s) => s.id) || []);
      setSelectedPartner(existingMatter.partners?.[0]?.id);
      form.reset({
        isCourtCase: existingMatter.isCourtCase ?? false,
        category: (existingMatter.category ?? undefined) as MatterFormValues['category'],
        caseNumber: existingMatter.caseNumber ?? '',
        plainTiff: existingMatter.plainTiff ?? '',
        defendant: existingMatter.defendant ?? '',
        description: existingMatter.description ?? '',
        judgeName: existingMatter.judgeName ?? '',
        courtName: existingMatter.courtName ?? '',
        courtEmail: existingMatter.courtEmail ?? '',
        courtPhone: normalizeUzbekPhone(existingMatter.courtPhone),
        clientStatus: existingMatter.clientStatus ?? 'ACCUSED',
        contractId: existingMatter.contractId,
        clientId: existingMatter.clientId,
        practiceAreaId: existingMatter.practiceAreaId as number | undefined as number,
        scopeIn: existingMatter?.scopeIn ?? '',
        scopeOut: existingMatter?.scopeOut ?? '',
        estimate: existingMatter?.estimate != null ? String(existingMatter.estimate) : '',
        budget: existingMatter?.budget != null ? String(existingMatter.budget) : '',
        targetEndDate: existingMatter?.targetEndDate
          ? format(new Date(existingMatter.targetEndDate), 'yyyy-MM-dd')
          : '',
        riskLevel: (existingMatter?.riskLevel ?? undefined) as MatterFormValues['riskLevel'],
      });
    }
  }, [existingMatter, form]);

  // Set contractId from URL param
  React.useEffect(() => {
    if (contractIdFromUrl && !isEditMode) {
      form.setValue('contractId', contractIdFromUrl, { shouldDirty: false });
    }
  }, [contractIdFromUrl, isEditMode, form]);

  const onSubmit: SubmitHandler<MatterFormValues> = async (values, action?: string) => {
    // Partner is required at creation unless the case is MD-managed (then the
    // whole team is optional); Lead/staff are always optional.
    if (!isEditMode && !isMdManaged && !selectedPartner) {
      toast.error(t('partnerRequired'));
      return;
    }

    // eslint_disable-next-line @typescript-eslint/no-explicit-any
    const rawPayload: Record<string, any> = {
      ...values,
      contractId: values.contractId,
      practiceAreaId: values.practiceAreaId ? Number(values.practiceAreaId) : undefined,
      // Optional plan fields — numbers travel as strings in the form; empty
      // values are stripped by removeEmptyFields below.
      estimate: values.estimate ? Number(values.estimate) : undefined,
      budget: values.budget ? Number(values.budget) : undefined,
      targetEndDate: values.targetEndDate
        ? new Date(values.targetEndDate).toISOString()
        : undefined,
      // Team is set only at creation. On update the team is managed from the
      // matter detail page, so we never send membership arrays here (avoids
      // re-triggering the backend's personnel/task reconciliation).
      ...(isEditMode
        ? {
            // Category is editable only while INTAKE (server rejects it later).
            // Send it only when actually changed — a legacy MIGRATION_PENDING
            // value must never be re-sent (the DTO rejects the sentinel).
            category:
              values.category !== existingMatter?.category
                ? values.category
                : undefined,
          }
        : {
            isMdManaged,
            // Omit the optional team arrays entirely when empty.
            responsibleAttorneys: selectedAttorney ? [selectedAttorney] : undefined,
            staffs: selectedStaffs.length > 0 ? selectedStaffs : undefined,
            partners: selectedPartner ? [selectedPartner] : [],
          }),
    };

    // Remove clientId from payload (not part of CreateMatterDto)
    delete rawPayload['clientId'];

    const payload = removeEmptyFields(rawPayload) as CreateMatterDto;

    if (isEditMode && id) {
      await updateMutation.mutateAsync({ id, data: payload });
      navigate(-1);
    } else {
      await createMutation.mutateAsync(payload);
      clearDraft();

      if (action === 'createAndContinue') {
        form.reset();
        setSelectedAttorney(undefined);
        setSelectedStaffs([]);
        setSelectedPartner(undefined);
        setIsMdManaged(false);
      } else {
        navigate(-1);
      }
    }
  };

  // Surface zod failures as a toast — some validated fields aren't rendered
  // (court fields on a non-court matter), so without this a failing submit
  // looks like a dead Save button.
  const onInvalid = (errors: FieldErrors<MatterFormValues>) => {
    const firstError = Object.values(errors)?.[0];
    const message = firstError?.message;
    toast.error(typeof message === 'string' ? message : t('checkFormFields'));
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return {
    form,
    onSubmit,
    onInvalid,
    navigate,
    isEditMode,
    isLoadingMatter,
    isPending,
    isCourtCase,
    lockCourtCase,
    lockPostIntake,
    contractIdFromUrl,
    selectedClientId,
    setSelectedClientId,
    selectedAttorney,
    setSelectedAttorney,
    selectedStaffs,
    setSelectedStaffs,
    selectedPartner,
    setSelectedPartner,
    isMdManaged,
    setIsMdManaged,
  };
}
